<template>
  <owa-modal-frame
    :is-showing="isModalReady && isShowing"
    :has-sections="false"
    :aria-label="ariaLabel"
    component-class="upsell"
    has-actions
    @close-modal="$emit('close-modal')"
  >
    <slot name="upsell-description">
      <div
        class="margin-bottom-xl display-flex flex-items-start"
        style="gap: var(--spacing-md)"
      >
        <span
          v-if="resolvedIconName"
          class="display-inline-flex flex-items-center align-center"
          :style="`
            flex-shrink: 0;
            width: 3rem;
            height: 3rem;
            border-radius: var(--border-radius-sm);
            background: color-mix(in oklab, var(--fa-${chipColor}), white 85%);
            color: var(--fa-dk-${chipColor});
          `"
        >
          <font-awesome-icon
            :icon="$v7[resolvedIconStyle][resolvedIconName]"
            size="xl"
          />
        </span>
        <div style="min-width: 0">
          <h2 class="margin-top-6xs margin-bottom-0 strong size-lg">
            {{ resolvedHeadline }}
          </h2>
          <p
            class="size-md margin-bottom-0"
            style="color: var(--fa-md-gravy)"
          >
            {{ resolvedSupport }}
          </p>
        </div>
      </div>
    </slot>

    <!-- No route-forward button here on purpose: each plan card carries its
         own routed action. Surfaces without cards use the mixin's link. -->
    <slot name="upsell-options">
      <upsell-options
        v-if="isShowing"
        :icon="icon"
        :is-custom-icon="isCustomIcon"
        :upsell-trigger-slug="gate"
        :override-target-skus="overrideTargetSkus"
        :context-plan-sku="contextPlanSku"
        @on-plans-ready="onPlansReady"
        @on-sku-count="onSkuCount"
      />
    </slot>

    <upsell-add-on-option
      v-if="showAddOnRow"
      class="margin-top-2xl"
      :resource="addOnAvailability.resource"
      :gate="gate"
      :preview="addOnPreview"
    />

    <upsell-add-on-option
      v-else-if="outcome === 'add-on'"
      :resource="addOnAvailability.resource"
      :gate="gate"
      :preview="addOnPreview"
      primary
    />

    <div
      v-else-if="noPlanMessage || showBillingOwnerNote"
      class="message margin-bottom-0"
      :class="{ 'margin-top-2xl': outcome === 'plans' }"
      style="--message-border-color: var(--fa-lt-gravy)"
    >
      <p
        v-if="showBillingOwnerNote"
        class="margin-y-0"
      >
        Ask the
        <site-link :to="{ name: 'account.team' }">person who manages billing</site-link>
        for this account to add {{ addOnObject }}.
      </p>
      <p
        v-else
        class="margin-y-0"
      >
        {{ noPlanMessage }}
        <a
          v-if="outcome === 'contact'"
          :href="talkToUsUrl"
          @click="$posthog.capture('talk_to_us_click', { context: 'upsell_modal', feature: gate, outcome })"
          >Contact Us</a
        >
      </p>
    </div>

    <template #actions>
      <div class="row size-sm muted align-center tablet:align-start tablet:text-left">
        <div class="column-12 tablet:column-6">
          <p>
            <site-link
              :to="{ name: 'plans' }"
              class="no-underline"
              @click="$posthog.capture('view_plans_button_click', { context: 'upsell_modal', feature: gate })"
              >See all plans</site-link
            >
          </p>
        </div>
        <div class="column-12 tablet:column-6 tablet:text-right">
          <p v-if="!isSignedIn">
            Already have a plan?
            <site-link
              :to="{ name: 'sessions.sign-in', query: { next: signInNext } }"
              class="no-underline"
              @click="$posthog.capture('sign_in_button_click', { context: 'upsell_modal', feature: gate })"
            >
              Sign in
            </site-link>
          </p>
          <p v-else>
            <a
              :href="talkToUsUrl"
              class="no-underline"
              @click="$posthog.capture('talk_to_us_click', { context: 'upsell_modal', feature: gate })"
              >Talk to us</a
            >
          </p>
        </div>
      </div>
    </template>
  </owa-modal-frame>
</template>

<script>
  import { ModalFrameMixin } from 'app/components-wa/modals/ModalFrame'
  import { EventTrackingMixin } from 'app/mixins'
  import { mapActions, mapGetters } from 'vuex'
  import path from 'path'

  import { PLAN_COLOR_BY_SKU } from 'app/views/plans/PlansData'
  import { DOWNLOAD_ENTITLEMENT_GATES, GATE_COPY, GATES, addOnResourceForGate, downloadBlockReason, upsellPolicy } from 'app/views/plans/upsellPolicy'
  import { AddOnEligibilityMixin } from 'app/views/accounts/addOns/addOnEligibility'
  import { currentUnitsFor, isBinaryFor, isSupportedFor, resourceLabel } from 'app/views/accounts/addOns/addOnResources'

  import UpsellAddOnOption from './UpsellAddOnOption'
  import UpsellOptions from './UpsellOptions'

  const TALK_TO_US_URL = 'mailto:hello@fontawesome.com?subject=Question about upgrading my Font Awesome plan'

  export default {
    name: 'ModalUpsell',

    components: {
      UpsellAddOnOption,
      UpsellOptions
    },

    mixins: [AddOnEligibilityMixin, ModalFrameMixin, EventTrackingMixin],

    props: {
      customStyle: {
        type: Object,
        default: () => {}
      },
      icon: {
        type: Object,
        default: null
      },
      isCustomIcon: {
        type: Boolean,
        default: false
      },
      isShowing: {
        type: Boolean,
        default: false
      },
      // The modal is driven entirely by a `gate` string (from GATES in
      // upsellPolicy.js). The policy derives headline, supporting copy,
      // target SKUs, and suppress-vs-show behavior.
      gate: {
        type: String,
        required: true,
        validator: (value) => Object.values(GATES).includes(value)
      },
      ariaLabel: {
        type: String,
        default: 'Upgrade required'
      }
    },

    data() {
      return {
        plansReady: false,
        kitsSettled: false,
        // One pack, priced by the server: the add-on row's price, and whether
        // another pack still fits under the add-on maximum.
        addOnPreview: null,
        addOnPreviewSettled: false,
        skuCount: 0,
        talkToUsUrl: TALK_TO_US_URL
      }
    },

    computed: {
      ...mapGetters('subscriptions', ['currentSubscription']),
      ...mapGetters('plans', ['getPlan']),
      ...mapGetters('kits', ['activeKits']),
      ...mapGetters('iconUploads', ['totalUploaded']),
      ...mapGetters('seatAssignments', ['seatAssignments']),

      isSignedIn() {
        return this.$route.meta.isSignedIn
      },

      signInNext() {
        // vue-router 4: the history instance lives under `options`, and
        // `$router.history` is undefined. Arrived via main, which is router 3.
        return path.join(this.$router.options.history.base, this.$route.fullPath)
      },

      // A canceled or expired account keeps its old plan record, so reading
      // the plan alone would report that the user still has whatever the gate
      // asks for and suppress the modal. Gate on the same determination
      // `SubscriptionMixin.proLocked` uses, so the suppressor can never
      // disagree with the callers that trigger it.
      entitledPlan() {
        return this.$dt.hasActivePaidSubscription ? this.currentSubscription?.plan || null : null
      },

      // Why downloads are off for this customer, if they are. Drives both the
      // the wording and the CTA, so the message and the route can never disagree.
      blockReason() {
        return downloadBlockReason({ currentPlan: this.entitledPlan, getPlan: this.getPlan })
      },

      // Policy resolves the gate against the user's current plan and the
      // seats they've actually assigned (so a legacy Pro user with 0
      // assignments gets nudged to Solo, not Team). `seatChangesAllowed` is
      // the authoritative seat gate — see `currentPlanSatisfiesGate`.
      policyResult() {
        return upsellPolicy({
          currentPlan: this.entitledPlan,
          gate: this.gate,
          currentSeatUsage: this.currentUsage.seats,
          seatChangesAllowed: this.currentSubscription?.seatChangesAllowed === true,
          getPlan: this.getPlan,
          ...this.policyInputs
        })
      },

      // What the customer has and could buy, for the policy's coverage checks.
      // Limits only count while the plan is entitled: a canceled account's old
      // plan still reports its limits, but they no longer apply.
      policyInputs() {
        const subscription = this.currentSubscription

        return {
          usage: { kits: this.currentUsage.kits, customIcons: this.currentUsage.customIcons },
          effectiveLimits: this.entitledPlan
            ? { kits: currentUnitsFor(subscription, 'kits'), customIcons: currentUnitsFor(subscription, 'icon_uploads') }
            : null,
          servesProPlus: this.entitledPlan != null && subscription?.servesProPlus === true,
          addOn: this.addOnAvailability
        }
      },

      outcome() {
        return this.policyResult?.outcome || null
      },

      gateCopy() {
        return GATE_COPY[this.gate] || null
      },

      // The tiers the policy is actually offering, so copy never hardcodes one.
      targetPlans() {
        return (this.policyResult?.targetSkus || []).map((sku) => this.getPlan?.(sku)).filter(Boolean)
      },

      targetPlanNames() {
        return this.targetPlans.map((plan) => plan.name).filter(Boolean)
      },

      resolvedHeadline() {
        return this.gateCopy?.headline || ''
      },

      resolvedSupport() {
        if (this.gateCopy) {
          const support = this.gateCopy.support
          return typeof support === 'function'
            ? support({
                getPlan: this.getPlan,
                blockReason: this.blockReason,
                currentPlan: this.entitledPlan,
                targetSkus: this.policyResult?.targetSkus || [],
                targetPlanNames: this.targetPlanNames,
                targetPlans: this.targetPlans,
                outcome: this.outcome
              })
            : support
        }
        return this.skuCount === 1 ? 'Choose the plan below to continue.' : 'Choose a plan below to continue.'
      },

      resolvedIconName() {
        return this.gateCopy?.icon || null
      },

      resolvedIconStyle() {
        return this.gateCopy?.iconStyle || 'solid'
      },

      // Tint the header icon chip to the target plan's accent color, using
      // the policy's first target SKU.
      chipColor() {
        const firstSku = this.policyResult?.targetSkus?.[0]
        if (firstSku && PLAN_COLOR_BY_SKU[firstSku]) return PLAN_COLOR_BY_SKU[firstSku]
        return PLAN_COLOR_BY_SKU['FA-SOLO'] || 'yellow'
      },

      // The policy's target SKUs drive the plan-card filtering in UpsellOptions.
      overrideTargetSkus() {
        return this.policyResult?.targetSkus?.length ? this.policyResult.targetSkus : null
      },

      // Cycle only: same tier either side, so the cards differ by one thing.
      // Cloud would mean three cards with tier and cycle both changing.
      contextPlanSku() {
        if (!DOWNLOAD_ENTITLEMENT_GATES.has(this.gate)) return null

        return this.blockReason === 'billing-cycle' ? this.entitledPlan?.sku || null : null
      },

      shouldSuppress() {
        return this.policyResult?.action === 'suppress'
      },

      // Whether this customer can buy their way out of the gate. The gate has to
      // be one an add-on satisfies (capacity, not capability), and `canAddOnFor`
      // answers for the customer, permission included. A subscription that can
      // hold add-ons but a user who can't manage billing gets pointed at whoever
      // can.
      addOnAvailability() {
        const resource = addOnResourceForGate(this.gate)
        if (!resource) return { resource: null, purchasable: false, blockedReason: null }

        if (this.canAddOnFor(resource)) {
          return this.addOnPreview?.exceedsMax === true
            ? { resource, purchasable: false, blockedReason: 'max-reached' }
            : { resource, purchasable: true, blockedReason: null }
        }

        // Only a plan that could hold the add-on has a billing owner worth asking.
        const ownerCouldBuy = this.addOnsAllowed && isSupportedFor(this.$subscription, resource)
        const blockedReason = ownerCouldBuy && !this.canManageBilling ? 'billing-owner' : 'ineligible'
        return { resource, purchasable: false, blockedReason }
      },

      showAddOnRow() {
        return this.outcome === 'plans' && this.addOnAvailability.purchasable
      },

      addOnNoun() {
        return resourceLabel(this.addOnAvailability.resource)
      },

      // What the add-on gets the customer: "more Kits", but Pro+ is had or not.
      addOnObject() {
        const resource = this.addOnAvailability.resource
        return isBinaryFor(resource) ? resourceLabel(resource) : `more ${this.addOnNoun}`
      },

      // A member who can't buy is told who can. A limit add-on says so only
      // when no plan fits, leaving the plan cards to speak otherwise; Pro+ says
      // so beneath the cards too, since the add-on is the smaller step than
      // a new plan.
      showBillingOwnerNote() {
        if (this.outcome === 'billing-owner') return true

        return this.outcome === 'plans' && this.addOnAvailability.blockedReason === 'billing-owner' && isBinaryFor(this.addOnAvailability.resource)
      },

      // The template renders billing-owner itself so it can link the phrase;
      // this string still gates the message box.
      noPlanMessage() {
        const noun = this.addOnNoun

        switch (this.outcome) {
          case 'billing-owner':
            return `Ask the person who manages billing for this account to add ${this.addOnObject}.`
          case 'contact':
            // Pro+ is the capability the customer asked about, not capacity, so
            // a plan that can't hold it hears why no plan was offered instead.
            if (!this.addOnAvailability.resource || (isBinaryFor(this.addOnAvailability.resource) && this.addOnAvailability.blockedReason === 'ineligible')) {
              return "None of our plans fit the Kits and Custom Icons you have now, but we'd be happy to help."
            }
            if (this.addOnAvailability.blockedReason === 'max-reached') return `You've added as many ${noun} as your plan allows. Need more?`
            return `We'd be happy to help you get ${this.addOnObject}.`
          default:
            return null
        }
      },

      // Plans and discounts come from UpsellOptions; kits give the usage the
      // policy filters on. Revealing before both would show cards that the
      // counts then remove.
      isModalReady() {
        return this.plansReady && this.kitsSettled && (!this.awaitsAddOnPreview || this.addOnPreviewSettled)
      },

      // With no plans to show, the preview decides between the add-on and a
      // message, so the modal holds until it lands. Beside plan cards it only
      // prices a row that can arrive late.
      awaitsAddOnPreview() {
        return !!addOnResourceForGate(this.gate) && !this.policyResult?.targetSkus?.length
      },

      // Whether a preview is worth asking for. Watched because the modal can
      // open before the subscription has loaded.
      canPreviewAddOn() {
        const resource = addOnResourceForGate(this.gate)
        return !!resource && this.canAddOnFor(resource)
      },

      // Current usage snapshot. Kits + custom icons are counts of in-use
      // resources; seats is the number of seats actually assigned to
      // teammates (relevant for legacy multi-seat users).
      //
      // Kits load when the modal opens (`loadKits`); until then the counts
      // read 0.
      currentUsage() {
        const assignedSeats = this.seatAssignments ? Object.keys(this.seatAssignments).length : 0
        return {
          kits: Array.isArray(this.activeKits) ? this.activeKits.length : 0,
          customIcons: this.totalUploaded || 0,
          seats: assignedSeats
        }
      }
    },

    watch: {
      // Watch both inputs because either can flip independently: subscription
      // data may load (changing shouldSuppress) before or after the caller
      // sets isShowing. A single watcher on shouldSuppress misses the case
      // where suppression resolves to true while the modal is still closed —
      // then opens later with no change to shouldSuppress to re-trigger.
      shouldSuppress: {
        immediate: true,
        handler() {
          this.maybeSuppress()
        }
      },

      canPreviewAddOn(canPreview) {
        if (canPreview && this.isShowing && !this.addOnPreview) this.loadAddOnPreview()
      },

      isShowing: {
        immediate: true,
        handler(showing) {
          // Reset readiness on close: UpsellOptions remounts on the next
          // open, and a stale ready flag reveals the frame before the fresh
          // mount has plans and discounts — the reopen flash.
          if (showing) {
            this.loadKits()
            this.loadAddOnPreview()
          } else {
            this.plansReady = false
            this.kitsSettled = false
            this.addOnPreview = null
            this.addOnPreviewSettled = false
          }

          this.maybeSuppress()
        }
      }
    },

    methods: {
      ...mapActions('kits', ['ensureKitsLoaded']),
      ...mapActions('subscriptions', ['previewAddOn']),

      // A failed fetch reveals the modal anyway: the server still enforces the
      // limits, so a stale count costs an imprecise offer, not access.
      async loadKits() {
        if (!this.isSignedIn) {
          this.kitsSettled = true
          return
        }

        try {
          await this.ensureKitsLoaded()
        } catch (error) {
          // Fall through with whatever the store already has.
        } finally {
          this.kitsSettled = true
        }
      },

      // Silent on failure, like the add-on section's savings line: the offer
      // falls back to unpriced copy, and the add-ons page reports anything that
      // actually blocks a purchase.
      async loadAddOnPreview() {
        if (!this.canPreviewAddOn) {
          this.addOnPreviewSettled = true
          return
        }

        this.addOnPreviewSettled = false

        try {
          this.addOnPreview = (await this.previewAddOn({ resource: addOnResourceForGate(this.gate), quantity: 1 })) || null
        } catch (error) {
          this.addOnPreview = null
        } finally {
          this.addOnPreviewSettled = true
        }
      },

      maybeSuppress() {
        if (this.shouldSuppress && this.isShowing) {
          // eslint-disable-next-line no-console
          console.warn(`[ModalUpsell] gate "${this.gate}" suppressed: current plan already covers this feature. Caller should not have triggered.`)
          this.$emit('close-modal')
        }
      },

      onPlansReady(fetched) {
        this.plansReady = fetched
      },

      onSkuCount(count) {
        this.skuCount = Number.isFinite(Number(count)) ? Number(count) : 0
      }
    }
  }
</script>
